import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { useAuthState } from '@/contexts/AuthContext';

export type EditCandidate = {
  namespace: string;
  key: string;
  currentValue: string;
};

export type EditTarget = {
  candidates: EditCandidate[];
  position?: { x: number, y: number };
};

type EditModeContextType = {
  isEditMode: boolean;
  setEditMode: (enabled: boolean) => void;
  canEdit: boolean;
  editTarget: EditTarget | null;
  setEditTarget: (target: EditTarget | null) => void;
  /** When true, UI should render as if user is not logged in (for admin preview) */
  isGuestView: boolean;
  setGuestView: (enabled: boolean) => void;
};

const EditModeContext = createContext<EditModeContextType | undefined>(undefined);

export const EditModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuthState();
  const canEdit = !!user && (user.role === 'admin' || user.role === 'root_admin');

  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [isGuestView, setIsGuestView] = useState<boolean>(false);

  useEffect(() => {
    // Restore persisted state
    try {
      const stored = localStorage.getItem('ui_text_edit_mode');
      if (stored) {
        setIsEditMode(stored === '1');
      }
      const storedGuestView = localStorage.getItem('ui_guest_view_mode');
      if (storedGuestView) {
        setIsGuestView(storedGuestView === '1');
      }
    } catch {}
  }, []);

  useEffect(() => {
    // Reset edit mode if permissions change
    if (!canEdit && isEditMode) {
      setIsEditMode(false);
      setEditTarget(null);
    }
  }, [canEdit, isEditMode]);

  // Stable callback for setEditMode
  const handleSetEditMode = useCallback((enabled: boolean) => {
    if (!canEdit) return;
    setIsEditMode(enabled);
    if (!enabled) setEditTarget(null);
    try {
      localStorage.setItem('ui_text_edit_mode', enabled ? '1' : '0');
    } catch {}
  }, [canEdit]);

  // Stable callback for setGuestView
  const handleSetGuestView = useCallback((enabled: boolean) => {
    if (!canEdit) return;
    setIsGuestView(enabled);
    try {
      localStorage.setItem('ui_guest_view_mode', enabled ? '1' : '0');
    } catch {}
  }, [canEdit]);

  const value = useMemo<EditModeContextType>(() => ({
    isEditMode,
    setEditMode: handleSetEditMode,
    canEdit,
    editTarget,
    setEditTarget,
    isGuestView,
    setGuestView: handleSetGuestView,
  }), [isEditMode, handleSetEditMode, canEdit, editTarget, isGuestView, handleSetGuestView]);

  return (
    <EditModeContext.Provider value={value}>
      {children}
    </EditModeContext.Provider>
  );
};

export const useEditMode = (): EditModeContextType => {
  const ctx = useContext(EditModeContext);
  if (!ctx) {
    throw new Error('useEditMode must be used within EditModeProvider');
  }
  return ctx;
};
